import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { webhooksApi } from '../../services/enterpriseApi';
import Modal from '../../components/common/Modal';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { LoadingScreen } from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import { FieldLabel } from '../../components/common/Tooltip';
import {
  Webhook,
  Plus,
  Trash2,
  Edit,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Send,
  Activity,
  TrendingUp,
  RefreshCw,
} from 'lucide-react';

export default function WebhooksConfiguration() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<any>(null);
  const queryClient = useQueryClient();

  const [newWebhook, setNewWebhook] = useState({
    name: '',
    url: '',
    events: [] as string[],
    secret: '',
    active: true,
    retry_config: {
      max_attempts: 3,
      backoff_multiplier: 2,
    },
  });

  const [testPayload, setTestPayload] = useState('{\n  "test": true\n}');

  const { data: webhooks, isLoading: webhooksLoading } = useQuery({
    queryKey: ['webhooks'],
    queryFn: () => webhooksApi.getAll(),
  });

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['webhook-stats'],
    queryFn: () => webhooksApi.getStats(),
  });

  const { data: deliveries } = useQuery({
    queryKey: ['webhook-deliveries', selectedWebhook?.id],
    queryFn: () => webhooksApi.getDeliveries(selectedWebhook.id, { limit: 20 }),
    enabled: !!selectedWebhook,
  });

  const { data: dlqItems } = useQuery({
    queryKey: ['webhook-dlq'],
    queryFn: () => webhooksApi.getDLQ({ limit: 50 }),
  });

  const createWebhookMutation = useMutation({
    mutationFn: (webhook: any) => webhooksApi.create(webhook),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      alert('Webhook created successfully');
      setShowCreateModal(false);
      setNewWebhook({
        name: '',
        url: '',
        events: [],
        secret: '',
        active: true,
        retry_config: { max_attempts: 3, backoff_multiplier: 2 },
      });
    },
  });

  const updateWebhookMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      webhooksApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      alert('Webhook updated successfully');
      setShowEditModal(false);
      setSelectedWebhook(null);
    },
  });

  const deleteWebhookMutation = useMutation({
    mutationFn: (id: string) => webhooksApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      alert('Webhook deleted successfully');
    },
  });

  const testWebhookMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) =>
      webhooksApi.test(id, payload),
    onSuccess: () => {
      alert('Test webhook sent successfully');
      setShowTestModal(false);
    },
  });

  const retryDeliveryMutation = useMutation({
    mutationFn: ({ webhookId, deliveryId }: { webhookId: string; deliveryId: string }) =>
      webhooksApi.retryDelivery(webhookId, deliveryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-deliveries'] });
      alert('Delivery retry initiated');
    },
  });

  const retryDLQMutation = useMutation({
    mutationFn: (itemId: string) => webhooksApi.retryDLQ(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-dlq'] });
      alert('DLQ item retry initiated');
    },
  });

  if (webhooksLoading || statsLoading) {
    return <LoadingScreen message="Loading webhooks..." />;
  }

  const statCards = [
    {
      title: 'Total Webhooks',
      value: stats?.data?.total_webhooks || 0,
      icon: Webhook,
      color: 'bg-primary-500',
    },
    {
      title: 'Active',
      value: stats?.data?.active_webhooks || 0,
      icon: CheckCircle,
      color: 'bg-green-500',
    },
    {
      title: 'Total Deliveries',
      value: stats?.data?.total_deliveries || 0,
      icon: Send,
      color: 'bg-blue-500',
    },
    {
      title: 'Failed (24h)',
      value: stats?.data?.failed_last_24h || 0,
      icon: XCircle,
      color: 'bg-red-500',
    },
  ];

  const availableEvents = [
    'mission.created',
    'mission.completed',
    'mission.failed',
    'analysis.completed',
    'decision.pending_review',
    'decision.certified',
    'decision.rejected',
    'alert.critical',
    'seal.tampered',
    'user.login',
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold text-gray-900 dark:text-white">
            Webhooks Configuration
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Configure webhooks to receive real-time event notifications
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Webhook
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{stat.title}</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stat.value}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Webhooks List */}
      <Card>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Configured Webhooks</h2>
        <div className="space-y-3">
          {webhooks?.data?.webhooks && webhooks.data.webhooks.length > 0 ? (
            webhooks.data.webhooks.map((webhook: any) => (
              <div
                key={webhook.id}
                className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Webhook className="w-5 h-5 text-primary-600" />
                      <h3 className="font-semibold text-gray-900 dark:text-white">{webhook.name}</h3>
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded ${
                          webhook.active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {webhook.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 font-mono">{webhook.url}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                      <span>{webhook.events?.length || 0} events</span>
                      <span>Max retries: {webhook.retry_config?.max_attempts || 3}</span>
                      <span className="flex items-center">
                        <Activity className="w-3 h-3 mr-1" />
                        {webhook.delivery_stats?.success_rate || 0}% success
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedWebhook(webhook);
                        setShowTestModal(true);
                      }}
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedWebhook(webhook);
                        setShowEditModal(true);
                      }}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (confirm('Are you sure you want to delete this webhook?')) {
                          deleteWebhookMutation.mutate(webhook.id);
                        }
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Recent Deliveries */}
                {selectedWebhook?.id === webhook.id && deliveries?.data?.deliveries && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h4 className="font-semibold text-sm text-gray-900 dark:text-white mb-2">
                      Recent Deliveries
                    </h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {deliveries.data.deliveries.map((delivery: any) => (
                        <div
                          key={delivery.id}
                          className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs"
                        >
                          <div className="flex items-center gap-2">
                            {delivery.status === 'success' ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : delivery.status === 'failed' ? (
                              <XCircle className="w-4 h-4 text-red-600" />
                            ) : (
                              <Clock className="w-4 h-4 text-yellow-600" />
                            )}
                            <span className="font-medium">{delivery.event_type}</span>
                            <span className="text-gray-500">
                              {new Date(delivery.created_at).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">
                              {delivery.response_code || 'N/A'}
                            </span>
                            {delivery.status === 'failed' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  retryDeliveryMutation.mutate({
                                    webhookId: webhook.id,
                                    deliveryId: delivery.id,
                                  })
                                }
                              >
                                <RefreshCw className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <Webhook className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-300">No webhooks configured</p>
              <Button className="mt-4" onClick={() => setShowCreateModal(true)}>
                Create Your First Webhook
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Dead Letter Queue */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Dead Letter Queue</h2>
          </div>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {dlqItems?.data?.items?.length || 0} failed deliveries
          </span>
        </div>

        <div className="space-y-2 max-h-96 overflow-y-auto">
          {dlqItems?.data?.items && dlqItems.data.items.length > 0 ? (
            dlqItems.data.items.map((item: any) => (
              <div
                key={item.id}
                className="p-3 border border-yellow-200 bg-yellow-50 rounded-lg"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm text-gray-900 dark:text-white">
                        {item.webhook_name}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{item.event_type}</span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-300 mb-1">
                      Failed after {item.attempts} attempts
                    </p>
                    <p className="text-xs text-red-600">{item.last_error}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Last attempt: {new Date(item.last_attempt_at).toLocaleString()}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => retryDLQMutation.mutate(item.id)}
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Retry
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-sm text-gray-600 dark:text-gray-300">No failed deliveries</p>
            </div>
          )}
        </div>
      </Card>

      {/* Create Webhook Modal */}
      {showCreateModal && (
        <Modal onClose={() => setShowCreateModal(false)} raw>
          <Card className="max-w-2xl w-full max-h-[calc(100dvh-2rem)] overflow-y-auto">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Create Webhook</h2>

            <div className="space-y-4">
              <div>
                <FieldLabel
                  label="Webhook Name"
                  tooltip="A descriptive name to identify this webhook configuration. Use names like 'Production', 'Staging', or 'Analytics'."
                  required
                  htmlFor="webhook-name"
                />
                <input
                  id="webhook-name"
                  type="text"
                  value={newWebhook.name}
                  onChange={(e) => setNewWebhook({ ...newWebhook, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Production Webhook"
                />
              </div>

              <div>
                <FieldLabel
                  label="Endpoint URL"
                  tooltip="The HTTPS endpoint where webhook events will be sent. Must return 200 OK for successful delivery. Supports POST requests only."
                  required
                  htmlFor="webhook-url"
                />
                <input
                  id="webhook-url"
                  type="url"
                  value={newWebhook.url}
                  onChange={(e) => setNewWebhook({ ...newWebhook, url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="https://api.example.com/webhooks"
                />
              </div>

              <div>
                <FieldLabel
                  label="Secret Key"
                  tooltip="Optional secret key for HMAC-SHA256 signature verification. The signature is sent in the X-Webhook-Signature header. Use this to verify webhook authenticity."
                  htmlFor="webhook-secret"
                />
                <input
                  id="webhook-secret"
                  type="password"
                  value={newWebhook.secret}
                  onChange={(e) => setNewWebhook({ ...newWebhook, secret: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Used for HMAC signature verification"
                />
              </div>

              <div>
                <FieldLabel
                  label="Subscribe to Events"
                  tooltip="Select which events trigger this webhook. Events include mission creation, analysis completion, integrity violations, and more. You can subscribe to multiple events."
                  required
                />
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                  {availableEvents.map((event) => (
                    <label key={event} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newWebhook.events.includes(event)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewWebhook({
                              ...newWebhook,
                              events: [...newWebhook.events, event],
                            });
                          } else {
                            setNewWebhook({
                              ...newWebhook,
                              events: newWebhook.events.filter((e) => e !== event),
                            });
                          }
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm">{event}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <FieldLabel
                    label="Max Retry Attempts"
                    tooltip="Number of times to retry failed webhook deliveries. Retries use exponential backoff. Set to 0 to disable retries. Recommended: 3-5 attempts."
                    htmlFor="max-attempts"
                  />
                  <input
                    id="max-attempts"
                    type="number"
                    value={newWebhook.retry_config.max_attempts}
                    onChange={(e) =>
                      setNewWebhook({
                        ...newWebhook,
                        retry_config: {
                          ...newWebhook.retry_config,
                          max_attempts: parseInt(e.target.value),
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    min="0"
                    max="10"
                  />
                </div>
                <div>
                  <FieldLabel
                    label="Backoff Multiplier"
                    tooltip="Exponential backoff multiplier for retries. Delay = base_delay * (multiplier ^ attempt). Higher values mean longer waits between retries. Recommended: 2.0"
                    htmlFor="backoff-multiplier"
                  />
                  <input
                    id="backoff-multiplier"
                    type="number"
                    value={newWebhook.retry_config.backoff_multiplier}
                    onChange={(e) =>
                      setNewWebhook({
                        ...newWebhook,
                        retry_config: {
                          ...newWebhook.retry_config,
                          backoff_multiplier: parseFloat(e.target.value),
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    min="1"
                    max="5"
                    step="0.5"
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  onClick={() => createWebhookMutation.mutate(newWebhook)}
                  disabled={
                    !newWebhook.name ||
                    !newWebhook.url ||
                    newWebhook.events.length === 0 ||
                    createWebhookMutation.isPending
                  }
                >
                  {createWebhookMutation.isPending ? 'Creating...' : 'Create Webhook'}
                </Button>
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        </Modal>
      )}

      {/* Test Webhook Modal */}
      {showTestModal && selectedWebhook && (
        <Modal onClose={() => setShowTestModal(false)} raw>
          <Card className="max-w-2xl w-full max-h-[calc(100dvh-2rem)] overflow-y-auto">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
              Test Webhook: {selectedWebhook.name}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Test Payload (JSON)
                </label>
                <textarea
                  value={testPayload}
                  onChange={(e) => setTestPayload(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                  rows={10}
                />
              </div>

              <Alert type="info">
                This will send a test webhook to {selectedWebhook.url}
              </Alert>

              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    try {
                      const payload = JSON.parse(testPayload);
                      testWebhookMutation.mutate({
                        id: selectedWebhook.id,
                        payload,
                      });
                    } catch (error) {
                      alert('Invalid JSON payload');
                    }
                  }}
                  disabled={testWebhookMutation.isPending}
                >
                  <Send className="w-4 h-4 mr-2" />
                  {testWebhookMutation.isPending ? 'Sending...' : 'Send Test'}
                </Button>
                <Button variant="outline" onClick={() => setShowTestModal(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        </Modal>
      )}
    </div>
  );
}

// Made with Bob
